// @ts-nocheck

import type { IMAnnualProgramPreviewProps } from '@medante/react-components/blocks/program/annual-program-preview'
import type { IMDividerImagesAProps } from '@medante/react-components/blocks/divider/divider-images-a'
import type { IMHeroAProps } from '@medante/react-components/blocks/hero/hero-a'
import type { IMTextAProps } from '@medante/react-components/blocks/text/text-a'

import type { IPageBuilderBlock } from './parse-page-builder-html'

function safeJson<T>(raw: string | undefined): T | undefined {
	if (raw == null || raw === '') return undefined
	try {
		return JSON.parse(raw) as T
	} catch {
		return undefined
	}
}

function heroAProps(attrs: Record<string, string>): IMHeroAProps | null {
	const dataImage = safeJson<{ url?: string; name?: string; fileName?: string }>(
		attrs['data-image']
	)
	const image =
		dataImage?.url != null
			? { src: dataImage.url, alt: dataImage.name ?? dataImage.fileName ?? '' }
			: undefined
	const buttons = safeJson<IMHeroAProps['buttons']>(attrs['data-buttons']) ?? []
	return {
		eyebrow: attrs.eyebrow,
		title: attrs.title ?? '',
		text: attrs.text ?? '',
		buttons,
		...(image && { image })
	}
}

function dividerImagesAProps(attrs: Record<string, string>): IMDividerImagesAProps | null {
	const highlights = safeJson<IMDividerImagesAProps['highlights']>(attrs['data-highlights'])
	if (!highlights?.length) return null
	return { highlights }
}

function textAProps(attrs: Record<string, string>): IMTextAProps | null {
	const reverse = attrs.reverse === 'true'
	const buttons = safeJson<IMTextAProps['buttons']>(attrs['data-buttons'])
	const dataImages = safeJson<Array<{ src: string; alt?: string }>>(attrs['data-images'])
	const images = dataImages?.map(img => ({ src: img.src, alt: img.alt ?? '' }))
	return {
		title: attrs.title,
		content: attrs.content ?? '',
		reverse,
		...(buttons?.length && { buttons }),
		...(images?.length && { images })
	}
}

function annualProgramPreviewAProps(
	attrs: Record<string, string>
): IMAnnualProgramPreviewProps | null {
	const apiBaseUrl = attrs['data-api-base-url']
	const annualProgramsConfig = safeJson<IMAnnualProgramPreviewProps['annualProgramsConfig']>(
		attrs['data-annual-programs-config']
	)
	const programSection = safeJson<IMAnnualProgramPreviewProps['programSection']>(
		attrs['data-program-section']
	)
	const modulesSectionRaw = safeJson<{
		title?: string
		text?: string
		footerText?: string
		showProgramsSwitch?: boolean
		image?: { url?: string; name?: string }
	}>(attrs['data-modules-section'])
	const modulesSection =
		modulesSectionRaw != null
			? {
					...modulesSectionRaw,
					...(modulesSectionRaw.image?.url && {
						image: {
							src: modulesSectionRaw.image.url,
							alt: modulesSectionRaw.image.name ?? ''
						}
					})
				}
			: undefined
	return {
		...(apiBaseUrl && { apiBaseUrl }),
		...(annualProgramsConfig?.length && { annualProgramsConfig }),
		...(programSection && { programSection }),
		...(modulesSection && { modulesSection })
	}
}

export type BlockPropsMap = {
	'hero-a': IMHeroAProps
	'divider-images-a': IMDividerImagesAProps
	'text-a': IMTextAProps
	'annual-program-preview-a': IMAnnualProgramPreviewProps
}

export type PageBuilderBlockProps =
	| IMHeroAProps
	| IMDividerImagesAProps
	| IMTextAProps
	| IMAnnualProgramPreviewProps

export function blockToProps(block: IPageBuilderBlock): PageBuilderBlockProps | null {
	switch (block.type) {
		case 'hero-a':
			return heroAProps(block.attrs)
		case 'divider-images-a':
			return dividerImagesAProps(block.attrs)
		case 'text-a':
			return textAProps(block.attrs)
		case 'annual-program-preview-a':
			return annualProgramPreviewAProps(block.attrs)
		default:
			return null
	}
}

export const PAGE_BUILDER_BLOCK_TYPES = [
	'hero-a',
	'divider-images-a',
	'text-a',
	'annual-program-preview-a'
] as const

export type PageBuilderBlockType = (typeof PAGE_BUILDER_BLOCK_TYPES)[number]
